<template>
  <div class="map_wrapper">
    <label v-if="placeName" class="place-label">{{ placeName }}</label>
    <GmapMap
      :center="{ lat: parseFloat(centerLat), lng: parseFloat(centerLng) }"
      :zoom="12"
      map-type-id="terrain"
      style="width: 100%; height: 400px"
      @click="onMapClick"
    >
      <GmapPolygon
        ref="polygon"
        :paths="paths"
        :editable="!readonly"
        :draggable="!readonly"
        :options="polygonOptions"
        @paths_changed="onPolygonChanged"
        @dragend="onPolygonChanged"
      />
    </GmapMap>

    <button
      v-if="!readonly"
      @click="resetPolygon"
      class="reset-btn"
      type="button"
    >
      {{ $t("MESSAGES.resetPolygon") }}
    </button>
  </div>
</template>

<script>
export default {
  name: "PolygonMap",
  props: {
    centerLat: {
      type: Number,
      required: true,
    },
    centerLng: {
      type: Number,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    initialPaths: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      placeName: "",
      paths: [],
      polygonOptions: {
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#FF0000",
        fillOpacity: 0.35,
      },
    };
  },

  mounted() {
    if (this.initialPaths && this.initialPaths.length > 0) {
      this.paths = [...this.initialPaths];
      this.$emit("update:paths", this.paths);

      // Get place name for the first coordinate if in readonly mode
      if (this.readonly && this.paths.length > 0) {
        this.getPlaceName(this.paths[0].lat, this.paths[0].lng);
      }
    }
  },

  watch: {
    initialPaths: {
      handler(newPaths) {
        if (newPaths && newPaths.length > 0) {
          this.paths = [...newPaths];
          this.$emit("update:paths", this.paths);

          // Get place name for the first coordinate if in readonly mode
          if (this.readonly && this.paths.length > 0) {
            this.getPlaceName(this.paths[0].lat, this.paths[0].lng);
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    onMapClick(event) {
      if (this.readonly) return;

      const clickedLat = event.latLng.lat();
      const clickedLng = event.latLng.lng();

      this.paths.push({ lat: clickedLat, lng: clickedLng });
      this.$emit("update:paths", this.paths);

      this.getPlaceName(clickedLat, clickedLng);
    },

    onPolygonChanged() {
      if (this.readonly || !this.$refs.polygon) return;

      // vue2-google-maps exposes the underlying Google Maps object as $mapObject
      const poly =
        this.$refs.polygon.$mapObject ||
        this.$refs.polygon.$polygonObject ||
        this.$refs.polygon.$polygon;

      if (!poly) return;

      let updated = [];
      try {
        // Prefer getPaths() to gracefully handle single or multi-path polygons
        if (typeof poly.getPaths === "function") {
          const pathsArr = poly.getPaths().getArray();
          if (pathsArr.length > 0) {
            updated = pathsArr[0].getArray().map((latLng) => ({
              lat: latLng.lat(),
              lng: latLng.lng(),
            }));
          }
        } else if (typeof poly.getPath === "function") {
          updated = poly
            .getPath()
            .getArray()
            .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
        }
      } catch (e) {
        // Fallback: keep current paths if something goes wrong
        updated = this.paths || [];
      }

      this.paths = updated;
      this.$emit("update:paths", this.paths);
    },

    async getPlaceName(lat, lng) {
      const geocoder = new google.maps.Geocoder();
      const latlng = { lat, lng };

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === "OK" && results[0]) {
          this.placeName = results[0].formatted_address;
        } else {
          this.placeName = "Unknown Location";
        }
        this.$emit("update:placeName", this.placeName);
      });
    },

    resetPolygon() {
      if (this.readonly) return;

      this.paths = [];
      this.$emit("update:paths", this.paths);
    },
  },
};
</script>

<style scoped>
.map_wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.place-label {
  display: block;
  margin: 20px 0;
  font-size: 16px;
  font-weight: bold;
}

.reset-btn {
  margin-top: 10px;
  padding: 8px 12px;
  background: #ff4444;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
</style>
